var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
var _a;
/* Utility functions for sign up flow. */
import actions from './actions';
import { FLEXIBLE_SECTIONS_TO_PROGRESS_STEPS_CONFIG, OTTO_SECTIONS_TO_PROGRESS_STEPS_CONFIG, PERSONALIZED_SECTIONS_TO_PROGRESS_STEPS_CONFIG, } from './enums';
import store from './store';
import { calculateNumBundles, determineRecipeError, getGenderPronouns } from '@utils/common_utils';
import { isInt } from '@utils/form_validations';
import { formatArrayCommaString } from '@utils/formatting_utils';
import { ADD_ON_PROBIOTICS_PRODUCT_TYPES, MAX_NUM_RECIPES_FOR_FLEXIBLE_FOOD_SIGNUP, PET_TYPE, RECIPE_MODE, TREATS_PRODUCT_TYPES, } from '@variables';
var _PROFILE_VERSION_TO_BASE_URL = {
    juicy: '/profile/',
    bab: '/profile/bab/',
    otto: '/profile/otto/',
};
/* Returns base url for profile version. */
var getBaseUrlForProfileVersion = function () {
    return _PROFILE_VERSION_TO_BASE_URL[store.get('version')] || '/profile/';
};
/* Gets the max number of pets that may be added based on promo code and profile version. */
var getMaxPets = function () {
    if (isOneTimeTrial()) {
        return 1;
    }
    return 6;
};
/* Gets a set of pet types for available pets. */
var getPetTypes = function (pets) { return new Set(pets.map(function (row) { return row.petType; })); };
/* Casts a string input value as an integer or null if not a number. */
var castAsInt = function (value) {
    return isNaN(parseInt(value, 10)) ? null : parseInt(value, 10);
};
/* Decodes special HTML characters. */
var decodeHtml = function (value) {
    return value ? value.replace(/&amp;/g, '&') : undefined;
};
/* Return boolean whether a product is already added */
var isAddedItem = function (orderItems, productType) {
    return orderItems.some(function (item) { return item.productType === productType && item.units > 0; });
};
var getAddOnProduct = function (productType) {
    return store
        .get('addOnProducts')
        .find(function (product) { return product.key.name === productType; });
};
var calculateAddOnSavings = function () {
    var savings = 0;
    var orderItems = store.get('orderItems');
    orderItems.length &&
        orderItems.forEach(function (item) {
            var product = getAddOnProduct(item.productType);
            if (product.shopifyCostCents) {
                savings += (item.units * (product.shopifyCostCents - product.costCents)) / 100;
            }
        });
    return savings;
};
var calculateAddOnCost = function () {
    return store
        .get('orderItems')
        .reduce(function (accumulator, item) { var _a; return accumulator + (item.units * (((_a = getAddOnProduct(item.productType)) === null || _a === void 0 ? void 0 : _a.costCents) || 0)) / 100; }, 0);
};
/**
 * Calculates number of add ons a profile has during signup.
 * @param {Array} orderItems add on items in order.
 * @param {boolean} recurringOnly Optional filter to count only recurring add ons.
 * @return {number} number of add on items.
 */
var countAddons = function (orderItems, recurringOnly) {
    if (recurringOnly === void 0) { recurringOnly = false; }
    var addOns = recurringOnly
        ? orderItems.filter(function (item) { return item.isRecurring === true; })
        : orderItems;
    return addOns.reduce(function (accumulator, item) { return accumulator + item.units; }, 0);
};
var allPetsHaveProperty = function (pets, propertyName) {
    return pets.every(function (pet) { return pet[propertyName]; });
};
/* Validates that number of bundles is within expected range */
// Each pet can select up to the max number of recipes (4) and will get 1 bundle per recipe
var isValidNumBundles = function (numBundles) {
    var pets = store.get('pets');
    return numBundles > 0 && numBundles <= MAX_NUM_RECIPES_FOR_FLEXIBLE_FOOD_SIGNUP * pets.length;
};
/* Checks that current delivery interval and number of bundles is valid and all pets have recipes */
var isValidFixedSkuProfile = function () {
    var pets = store.get('pets');
    if (!store.get('deliveryInterval') || !allPetsHaveProperty(pets, 'recipe')) {
        return false;
    }
    return isValidNumBundles(calculateNumBundles(pets));
};
var isValidOneTimeTrialProfile = function () {
    var orderItems = store.get('orderItems');
    return orderItems && orderItems.length === 1;
};
var isBuildABowl = function () { return store.get('version') === 'bab'; };
var isOneTimeTrial = function () { return store.get('version') === 'otto'; };
var isJuicy = function () { return store.get('version') === 'juicy'; };
/* Check if user has enough data to render a summary view (recipes needed for bab summary). */
var hasValidSummary = function () {
    var pets = store.get('pets');
    if (!pets || !pets.length || !allPetsHaveProperty(pets, 'mealPlanItems')) {
        return false;
    }
    if (!store.get('state') || !store.get('zipcode')) {
        return false;
    }
    if (isBuildABowl() && !isValidFixedSkuProfile()) {
        return false;
    }
    if (isOneTimeTrial() && !isValidOneTimeTrialProfile()) {
        return false;
    }
    // All subscription based profiles should have pets with recipes selected.
    if (!isOneTimeTrial() && !allPetsHaveProperty(pets, 'recipe')) {
        return false;
    }
    // All profiles should have a non-zero subtotal (i.e. cost of subscription food + order items).
    return store.get('email') && store.get('orderCosts') && store.get('orderCosts').subtotal;
};
/* Formats recipes for human-readable display text. */
var formatRecipesText = function (recipes, recipesMap, conjunction) {
    return formatArrayCommaString(recipes.map(function (value) { return recipesMap[value].displayName; }), conjunction);
};
var convertGramsToPounds = function (grams) {
    var POUNDS_IN_A_GRAM = 0.00220462;
    return grams * POUNDS_IN_A_GRAM;
};
var determineInitialWeightStatus = function (pet) {
    if (!pet.weight) {
        return;
    }
    if (pet.currentWeight === pet.weight) {
        return 'ideal';
    }
    if (pet.currentWeight > pet.weight) {
        return 'overweight';
    }
    if (pet.currentWeight < pet.weight) {
        return 'underweight';
    }
};
var getRecipeSelectionValidationMessage = function (selectedRecipes, mealsPerDay, daysOfFood, numRecipes) {
    var recipeMode = selectedRecipes.length > 1 ? RECIPE_MODE.MIX : RECIPE_MODE.FIXED;
    return determineRecipeError(recipeMode, selectedRecipes, daysOfFood, mealsPerDay, numRecipes);
};
/* Get copy of currently selected recipes for a pet. */
var getSelectedRecipes = function (pet) {
    if (pet.recipeMix.length) {
        return __spreadArray([], pet.recipeMix, true);
    }
    else if (pet.recipeRotation.length) {
        return __spreadArray([], pet.recipeRotation, true);
    }
    else if (pet.recipe) {
        return [pet.recipe];
    }
    return [];
};
/* Sets a list of recommended recipes based on selected purchasing motivations. */
var getRecommendedRecipes = function (purchasingMotivations, maxRecipes, isOverweight) {
    if (isOverweight === void 0) { isOverweight = false; }
    var recipes = new Set();
    if (purchasingMotivations.includes('picky_eater')) {
        recipes.add('chicken');
    }
    if (purchasingMotivations.includes('coat_issues')) {
        recipes.add('turkey_chicken');
    }
    if (purchasingMotivations.includes('no_issues')) {
        recipes.add('chicken');
    }
    if (isOverweight) {
        recipes.add('turkey');
    }
    if (purchasingMotivations.includes('allergies')) {
        recipes.add('pork');
        recipes.add('lamb');
        recipes.delete('beef');
        recipes.delete('chicken');
        recipes.delete('turkey_chicken');
    }
    // Restrict to a max of recipes to be selected.
    return Array.from(recipes).slice(0, maxRecipes);
};
/* Save recipe, recipe mode, and recipe mix to store. */
var saveRecipeSettings = function (selectedRecipes, petIndex) {
    // Set the actively selected recipe for the pet.
    if (selectedRecipes.length) {
        actions.handleSavePet(petIndex, 'recipe', selectedRecipes[0]);
    }
    else {
        actions.handleSavePet(petIndex, 'recipe', null);
    }
    // Set recipe mode to MIX or FIXED based on the number of selected recipes.
    if (selectedRecipes.length > 1) {
        actions.handleSavePet(petIndex, 'recipeMode', RECIPE_MODE.MIX);
        actions.handleSavePet(petIndex, 'recipeMix', selectedRecipes);
    }
    else {
        actions.handleSavePet(petIndex, 'recipeMode', RECIPE_MODE.FIXED);
        actions.handleSavePet(petIndex, 'recipeMix', []);
    }
};
/* Handles changes to add on order items, directly mutating orderItems. */
var handleAddOnItemChange = function (orderItems, productType, name, value) {
    var currentItem = orderItems.find(function (item) { return item.productType === productType; });
    // Create a new item if item does not already exist in store.
    if (!currentItem) {
        currentItem = {
            productType: productType,
            units: 0,
            isRecurring: true,
        };
        orderItems.push(currentItem);
    }
    if (name === 'isRecurring') {
        currentItem.isRecurring = Boolean(value);
    }
    else if (name === 'unitsRelative') {
        currentItem.units += Number(value);
    }
    else if (name === 'unitsAbsolute') {
        value = Number(value);
        if (isInt(value)) {
            currentItem.units = value;
        }
    }
    if (['unitsRelative', 'unitsAbsolute'].includes(name) && currentItem.units < 0) {
        currentItem.units = 0;
    }
};
// Gets the index for the given pet, defaults to 0 (first pet)
// Allows for more dynamic navigation with GoBackLink, ProgressSteps
var getPetIndex = function (petName, pets) {
    var petIndex = pets.findIndex(function (pet) { return pet.name === petName; });
    return petIndex > -1 ? petIndex : 0;
};
// Checks if a pet is valid, meaning has a name and weight set
var isValidPet = function (pet) {
    return Boolean(pet && pet.name && pet.currentWeight);
};
// Finds pets that have a missing property
var findPetProfileWithoutProperty = function (pets, propertyName) {
    var incompletePetIndex;
    var incompletePetProfile = pets.find(function (pet, index) {
        if (isValidPet(pet) && !pet[propertyName]) {
            incompletePetIndex = index;
            return pet;
        }
    });
    return [incompletePetProfile, incompletePetIndex];
};
// Returns the farthest progress percentage the user has made it to, even on refresh
// storedPercentage works for user jumping around in the same session
// config percentage works for refresh
var getProgressPercentage = function (storedPercentage, componentPercentage) {
    var pets = store.get('pets');
    var firstPet = pets && pets.length ? pets[0] : null;
    if (isOneTimeTrial()) {
        if (!store.get('customerName') || !store.get('email') || !isValidPet(firstPet)) {
            return Math.max(storedPercentage, componentPercentage, OTTO_SECTIONS_TO_PROGRESS_STEPS_CONFIG.you.percentagesByStep[1]);
        }
        if (!store.get('state') || !store.get('zipcode')) {
            return Math.max(storedPercentage, componentPercentage, OTTO_SECTIONS_TO_PROGRESS_STEPS_CONFIG.address.percentagesByStep[1]);
        }
        return Math.max(storedPercentage, componentPercentage, OTTO_SECTIONS_TO_PROGRESS_STEPS_CONFIG.order.percentagesByStep[1]);
    }
    var progressStepsConfigToUse = isBuildABowl()
        ? FLEXIBLE_SECTIONS_TO_PROGRESS_STEPS_CONFIG
        : PERSONALIZED_SECTIONS_TO_PROGRESS_STEPS_CONFIG;
    if (!isValidPet(firstPet)) {
        return Math.max(storedPercentage, componentPercentage, progressStepsConfigToUse.dogs.percentagesByStep[1]);
    }
    if (firstPet.name && !firstPet.currentWeight) {
        return Math.max(storedPercentage, componentPercentage, progressStepsConfigToUse.dogs.percentagesByStep[2]);
    }
    if (!firstPet.recipe) {
        return Math.max(storedPercentage, componentPercentage, progressStepsConfigToUse.meals.percentagesByStep[1]);
    }
    if (!store.get('customerName') || !store.get('email')) {
        return Math.max(storedPercentage, componentPercentage, progressStepsConfigToUse.you.percentagesByStep[1]);
    }
    if (!store.get('state') || !store.get('zipcode')) {
        return Math.max(storedPercentage, componentPercentage, progressStepsConfigToUse.you.percentagesByStep[2]);
    }
    return Math.max(storedPercentage, componentPercentage, progressStepsConfigToUse.order.percentagesByStep[1]);
};
// Returns recommended recipes for given pet based on purchasing motivations response
// and max number of recipes
var determineRecommendedRecipes = function (pet, petSurvey, maxNumRecipes) {
    var recommendedRecipes = [];
    if (petSurvey && petSurvey.purchasingMotivations) {
        recommendedRecipes = getRecommendedRecipes(petSurvey.purchasingMotivations, maxNumRecipes, pet.currentWeight > pet.weight);
    }
    return recommendedRecipes.length > 0 ? recommendedRecipes : ['chicken'];
};
// Returns the appropriate next step link after the recipe selection page, which should be an offer
// for an addon that isn't already added or the contact/address page
var getNextStepLink = function (pet, petIndex, purchasingMotivations, orderItems) {
    var baseUrl = getBaseUrlForProfileVersion();
    if (pet.petType === PET_TYPE.DOG) {
        var hasFreeTreatsInCart = isAddedItem(orderItems, TREATS_PRODUCT_TYPES.beefJerkyTreats4ozFree) ||
            isAddedItem(orderItems, TREATS_PRODUCT_TYPES.chickenJerkyTreats4ozFree);
        if (purchasingMotivations.includes('no_issues') &&
            !isAddedItem(orderItems, TREATS_PRODUCT_TYPES.beefJerkyTreats4oz) &&
            !hasFreeTreatsInCart) {
            return "".concat(baseUrl, "pet/offers/").concat(petIndex);
        }
        if ((pet.currentWeight > pet.weight || purchasingMotivations.includes('coat_issues')) &&
            !isAddedItem(orderItems, ADD_ON_PROBIOTICS_PRODUCT_TYPES.dogFullSpectrum)) {
            return "".concat(baseUrl, "pet/offers/").concat(petIndex);
        }
        if (purchasingMotivations.includes('allergies') &&
            !isAddedItem(orderItems, ADD_ON_PROBIOTICS_PRODUCT_TYPES.dogGITargeted)) {
            return "".concat(baseUrl, "pet/offers/").concat(petIndex);
        }
        if (purchasingMotivations.includes('picky_eater') &&
            !isAddedItem(orderItems, TREATS_PRODUCT_TYPES.chickenJerkyTreats4oz) &&
            !hasFreeTreatsInCart) {
            return "".concat(baseUrl, "pet/offers/").concat(petIndex);
        }
        // By default, if probiotic dog gi is not added to cart, we should recommend it
        if (!isAddedItem(orderItems, ADD_ON_PROBIOTICS_PRODUCT_TYPES.dogGITargeted)) {
            return "".concat(baseUrl, "pet/offers/").concat(petIndex);
        }
    }
    return "".concat(baseUrl).concat(isBuildABowl() ? 'address/' : 'contact/');
};
// Determine a probable puppy status based on birthday and age inputs
// If inputs are valid and the age is less than 1 year and 6 months, return true
// Otherwise return false
var determineIsLikelyPuppy = function (birthdayYear, birthdayMonth, ageYear, ageMonth) {
    if (birthdayYear && birthdayMonth && (ageYear < 1 || (ageYear === 1 && ageMonth <= 6))) {
        return true;
    }
    return false;
};
var ADD_ON_PRODUCT_DETAILS = (_a = {},
    _a[ADD_ON_PROBIOTICS_PRODUCT_TYPES.dogFullSpectrum] = {
        product: ADD_ON_PROBIOTICS_PRODUCT_TYPES.dogFullSpectrum,
        isTreat: false,
        isSupplement: true,
    },
    _a[ADD_ON_PROBIOTICS_PRODUCT_TYPES.dogGITargeted] = {
        product: ADD_ON_PROBIOTICS_PRODUCT_TYPES.dogGITargeted,
        isTreat: false,
        isSupplement: true,
    },
    _a[TREATS_PRODUCT_TYPES.beefJerkyTreats4oz] = {
        product: TREATS_PRODUCT_TYPES.beefJerkyTreats4oz,
        isTreat: true,
        isSupplement: false,
    },
    _a[TREATS_PRODUCT_TYPES.chickenJerkyTreats4oz] = {
        product: TREATS_PRODUCT_TYPES.chickenJerkyTreats4oz,
        isTreat: true,
        isSupplement: false,
    },
    _a);
// Returns suggested add-on(s) based on pet weight status or purchasing motivations
var getSuggestedAddOns = function (pet, petSurvey, orderItems) {
    var petPronouns = getGenderPronouns(pet.gender);
    var isOverweight = pet.currentWeight > pet.weight;
    if (isOverweight && !isAddedItem(orderItems, ADD_ON_PROBIOTICS_PRODUCT_TYPES.dogFullSpectrum)) {
        return __assign(__assign({}, ADD_ON_PRODUCT_DETAILS[ADD_ON_PROBIOTICS_PRODUCT_TYPES.dogFullSpectrum]), { description: "Did you know that ".concat(pet.name, "\u2019s gut, or microbiome, has a huge impact on\n            ").concat(petPronouns.adjective, " overall wellness? That means when it\u2019s out of sorts, it can\n            lead to issues like a sensitive stomach or flaky skin. Nom Nom\u2019s Full Spectrum\n            Probiotics were developed by vet nutritionists to restore and balance the microbiome.\n            Simply add a scoop to one of ").concat(pet.name, "\u2019s meals each day to support\n            ").concat(petPronouns.adjective, " digestion, immunity, and all-around health.") });
    }
    if (petSurvey && petSurvey.purchasingMotivations) {
        if (petSurvey.purchasingMotivations.includes('allergies') &&
            !isAddedItem(orderItems, ADD_ON_PROBIOTICS_PRODUCT_TYPES.dogGITargeted)) {
            return __assign(__assign({}, ADD_ON_PRODUCT_DETAILS[ADD_ON_PROBIOTICS_PRODUCT_TYPES.dogGITargeted]), { description: "Dog parents probably know a lot more about their pup\u2019s digestive system\n              than they want to. In that, they definitely know when it\u2019s out of whack. Nom Nom\u2019s\n              GI-Targeted Probiotics were developed by vet nutritionists to restore and balance\n              ".concat(pet.name, "\u2019s microbiome. Just add a scoop to one of ").concat(petPronouns.adjective, " daily\n              meals to help manage GI issues and support digestive health.") });
        }
        if (petSurvey.purchasingMotivations.includes('coat_issues') &&
            !isAddedItem(orderItems, ADD_ON_PROBIOTICS_PRODUCT_TYPES.dogFullSpectrum)) {
            return __assign(__assign({}, ADD_ON_PRODUCT_DETAILS[ADD_ON_PROBIOTICS_PRODUCT_TYPES.dogFullSpectrum]), { description: "Did you know that ".concat(pet.name, "\u2019s gut, or microbiome, has a huge impact on\n              ").concat(petPronouns.adjective, " overall wellness? That means when it\u2019s out of sorts, it can\n              lead to issues like a sensitive stomach or flaky skin. Nom Nom\u2019s Full Spectrum\n              Probiotics were developed by vet nutritionists to restore and balance the microbiome.\n              Simply add a scoop to one of ").concat(pet.name, "\u2019s meals each day to support\n              ").concat(petPronouns.adjective, " digestion, immunity, and all-around health.") });
        }
        var hasFreeTreatsInCart = isAddedItem(orderItems, TREATS_PRODUCT_TYPES.beefJerkyTreats4ozFree) ||
            isAddedItem(orderItems, TREATS_PRODUCT_TYPES.chickenJerkyTreats4ozFree);
        if (petSurvey.purchasingMotivations.includes('picky_eater') &&
            !isAddedItem(orderItems, TREATS_PRODUCT_TYPES.chickenJerkyTreats4oz) &&
            !hasFreeTreatsInCart) {
            return __assign(__assign({}, ADD_ON_PRODUCT_DETAILS[TREATS_PRODUCT_TYPES.chickenJerkyTreats4oz]), { description: "You\u2019ve got the healthy diet down, so why stop when it comes to snacks?\n              Our high-protein jerky is made with 100% lean chicken and nothing more, and can be\n              broken into bites for training, or enjoyed as a treat if ".concat(pet.name, " has been an\n              especially good ").concat(petPronouns.noun, ". And let\u2019s be real\u2026of course ").concat(petPronouns.subject, " has.") });
        }
        if (petSurvey.purchasingMotivations.includes('no_issues') &&
            !isAddedItem(orderItems, TREATS_PRODUCT_TYPES.beefJerkyTreats4oz) &&
            !hasFreeTreatsInCart) {
            return __assign(__assign({}, ADD_ON_PRODUCT_DETAILS[TREATS_PRODUCT_TYPES.beefJerkyTreats4oz]), { description: "Treat ".concat(pet.name, " like the good ").concat(petPronouns.noun, " ").concat(petPronouns.subject, "\n            is. Our high-protein jerky is made from 100% premium beef. No freaky fillers or funky\n            stuff, just like ").concat(petPronouns.adjective, " vet-developed food. Break into pieces for a tasty\n            training sesh, or offer one as a rewarding snack. Because the best dog (yours) deserves\n            the best treats (ours).") });
        }
    }
    return __assign(__assign({}, ADD_ON_PRODUCT_DETAILS[ADD_ON_PROBIOTICS_PRODUCT_TYPES.dogGITargeted]), { description: "Dog parents probably know a lot more about their pup\u2019s digestive system\n          than they want to. In that, they definitely know when it\u2019s out of whack. Nom Nom\u2019s\n          GI-Targeted Probiotics were developed by vet nutritionists to restore and balance\n          ".concat(pet.name, "\u2019s microbiome. Just add a scoop to one of ").concat(petPronouns.adjective, " daily\n          meals to help manage GI issues and support digestive health.") });
};
export { allPetsHaveProperty, calculateAddOnCost, calculateAddOnSavings, castAsInt, convertGramsToPounds, countAddons, decodeHtml, determineInitialWeightStatus, determineIsLikelyPuppy, determineRecommendedRecipes, findPetProfileWithoutProperty, formatRecipesText, getAddOnProduct, getBaseUrlForProfileVersion, getMaxPets, getNextStepLink, getPetIndex, getPetTypes, getProgressPercentage, getRecipeSelectionValidationMessage, getRecommendedRecipes, getSelectedRecipes, getSuggestedAddOns, handleAddOnItemChange, hasValidSummary, isAddedItem, isBuildABowl, isJuicy, isOneTimeTrial, isValidFixedSkuProfile, isValidNumBundles, isValidPet, saveRecipeSettings, };
